import { Fragment, useState, type ReactNode } from "react";
import { clearUsageLimit, getHealth, type ApiError, type HealthCard } from "../api";
import { useShell } from "../Layout";
import { useAsync } from "../useAsync";
import {
  ago, Badge, Card, CardBody, CardHead, DASH, ErrorBox, Freshness, Pane, State, usd, When,
} from "../ui";
import { clock, clockOrDay, dayAndTime, Gauge, pct, since } from "../usage";

export function HealthTab() {
  const { courseId } = useShell();
  const q = useAsync(() => getHealth(courseId), [courseId]);
  // 🔴 THE BANNER FIRED ONLY ON `STOP`, AND EVERY CONDITION THAT HALTS GRADING AFTER THE
  // SYSTEM HAS ONCE WORKED IS `WARN`. A stale poller cursor, a silent grade collector, a
  // paused usage cap — all WARN, because "it ran before" is not "it is running". So a fully
  // halted console rendered as amber detail rows with no alarm at all, on the one screen
  // whose entire job is to say something is wrong.
  // ⚠️ TONED, NOT PROMOTED. A WARN banner is amber and says WARN; turning it red would make
  // "degraded" and "stopped" indistinguishable, which is the same failure in the other
  // direction. STOP still wins if both are present.
  const cards = q.data?.cards ?? [];
  const worst = cards.find((c) => c.state === "STOP") ?? cards.find((c) => c.state === "WARN");
  const others = cards.filter((c) => c !== worst && c.state !== "OK").length;

  return (
    <div className="stack">
      {/* An alarm goes at the top or it is not an alarm. */}
      {worst && (
        <div className={worst.state === "STOP" ? "alert refused" : "alert"} role="alert">
          <span className="ico">{worst.state === "STOP" ? "⛔" : "⚠️"}</span>
          <div className="body">
            <div className="t">{worst.name}
              {others > 0 && <span className="muted"> · and {others} other card
                {others === 1 ? "" : "s"} not OK</span>}</div>
            <div className="m">{worst.detail}</div>
          </div>
        </div>)}

      <Card>
        <CardHead
          title="Health"
          desc={<>🔴 <strong>Registered is not reachable.</strong> These cards report what is
                configured and, where they can, what actually answered — an engine whose
                backend is down is still listed and still fails at dispatch.</>}
          right={<Freshness q={q} />} />
        <CardBody tight>
          <Pane q={q}>{(d) => (
            <div>
              {d.cards.map((c) => (
                <Reading key={c.name} c={c}
                         action={c.name === "Claude usage limit" && s(c.route)
                           && d.may_clear_usage_limit
                           ? <ClearLimit courseId={courseId} route={s(c.route)}
                                         onDone={q.reload} />
                           : null} />))}
            </div>)}
          </Pane>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * One card, with its structured figures rendered rather than dumped.
 *
 * 🔴 EVERY NUMBER ON THIS PAGE WAS INSIDE A `JSON.stringify`. Usage against its cap, how long
 * since the collector ticked, how many runs are uncollected, which worker groups answered,
 * which scopes the bot credential carries — all of it typed, all of it on the wire, and all
 * of it behind a disclosure labelled "readings" as raw JSON. The one page whose entire job
 * is to say what is wrong made a person read a blob to find out.
 *
 * ⚠️ THE BLOB SURVIVES FOR KEYS NOTHING CLAIMED. A card that grows a field must still show
 * it — rendering only the known keys would hide the next one silently, which is the failure
 * this page exists to prevent, one level up.
 */
function Reading({ c, action }: { c: HealthCard; action?: ReactNode }) {
  const [facts, used] = factsFor(c);
  const extra = Object.entries(c).filter(
    ([k]) => !["name", "state", "detail"].includes(k) && !used.has(k));
  return (
    <div className="reading">
      <div>
        <div className="head"><State s={c.state} />{c.name}</div>
        <div className="detail">{c.detail}</div>
        {extra.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary>other readings</summary>
            <pre className="pre" style={{ marginTop: 6 }}>
              {JSON.stringify(Object.fromEntries(extra), null, 2)}</pre>
          </details>)}
      </div>
      <div className="facts">{facts}{action}</div>
    </div>
  );
}

/**
 * Lift the usage pause by hand.
 *
 * 🔴 THE PAUSE HAD NO RELEASE A PERSON COULD REACH: it lifts when its reset passes or when a
 * later run stores a grade, and the day somebody knows better — the plan was upgraded, the
 * named reset was wrong — this card said when grading would resume and offered nothing.
 * ⚠️ NO CONFIRMATION STEP, BECAUSE BEING WRONG IS CHEAP. A window that has not reopened
 * re-arms the pause on the first run, and that run counts against nobody. The sentence says
 * so beside the button, where it decides the click.
 */
function ClearLimit({ courseId, route, onDone }:
  { courseId: string; route: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [note, setNote] = useState("");
  const clear = async () => {
    setBusy(true);
    setError(null);
    try {
      setNote((await clearUsageLimit(courseId, route)).note);
      onDone();
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack" style={{ marginTop: 10, gap: 6 }}>
      <div>
        <button type="button" className="btn sm" disabled={busy} onClick={clear}>
          {busy ? "Clearing…" : "Clear the pause now"}</button>
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        For every cohort. If the credential is still limited, the first run says so and the
        pause comes back by itself.</p>
      {note && <p className="small" role="status" style={{ margin: 0 }}>{note}</p>}
      {error && <ErrorBox err={error} />}
    </div>
  );
}

const n = (v: unknown) => (typeof v === "number" ? v : Number(v ?? 0));
const s = (v: unknown) => (typeof v === "string" ? v : "");

/** What each card knows how to show, and which of its keys that consumed. */
function factsFor(c: HealthCard): [ReactNode, Set<string>] {
  const kv = (rows: [string, ReactNode][]) => (
    <dl className="kv">{rows.map(([k, v]) => (
      <Fragment key={k}><dt>{k}</dt><dd>{v}</dd></Fragment>))}</dl>);

  switch (c.name) {
    case "Classroom poller":
      return [kv([
        ["last swept", <>{ago(n(c.age_seconds))}</>],
        ["failures in a row", n(c.consecutive_failures)],
        ["submissions seen", n(c.seen)],
        ["last error", s(c.last_error) || <span className="muted">{DASH}</span>],
      ]), new Set(["age_seconds", "consecutive_failures", "seen", "last_error"])];

    case "Grade collector":
      return [kv([
        ["heartbeat", <>{ago(n(c.age_seconds))}</>],
        // 🔴 THE SYMPTOM WORTH COUNTING. The sweeper can be alive and refusing every
        // envelope, which a fresh heartbeat alone renders as health.
        ["uncollected", n(c.uncollected) > 0
          ? <Badge tone="warn">{n(c.uncollected)}</Badge>
          : <span className="muted">none</span>],
        // ⚠️ COLLECTED AND NOT YET A GRADE: finalising failed for a reason of ours and the
        // collector tries again. The Runs page says when, run by run.
        ["held", n(c.held) > 0
          ? <><Badge tone="held">{n(c.held)}</Badge>{c.next_retry_at
              ? <> next <When iso={s(c.next_retry_at)} /></> : null}</>
          : <span className="muted">none</span>],
      ]), new Set(["age_seconds", "uncollected", "held", "next_retry_at"])];

    case "Claude usage limit":
      return [kv([
        // 🔴 WHEN IT ENDS IS THE WHOLE READING. A pause with no end on screen is a queue that
        // looks stopped.
        ["resets", c.resets_at ? <When iso={s(c.resets_at)} />
                               : <span className="muted">no limit in force</span>],
        ["known by", s(c.resets_source) === "parsed" ? "the credential's own message"
          : s(c.resets_source) === "fallback" ? <span className="warn">estimate — no time named</span>
          : <span className="muted">{DASH}</span>],
        ["hit", c.detected_at ? <When iso={s(c.detected_at)} /> : <span className="muted">{DASH}</span>],
        ["waiting", n(c.waiting) > 0
          ? <Badge tone="warn">{n(c.waiting)} in {n(c.cohorts)} cohort{n(c.cohorts) === 1 ? "" : "s"}</Badge>
          : <span className="muted">none</span>],
      ]), new Set(["resets_at", "resets_source", "detected_at", "waiting", "cohorts"])];

    case "Windmill workers": {
      const groups = Array.isArray(c.groups) ? c.groups as Record<string, unknown>[] : [];
      return [groups.length === 0 ? <span className="muted">no reading</span> : (
        <table className="mini">
          <thead><tr><th>Group</th><th className="num">Workers</th><th>Tags</th>
                     <th className="num">Last ping</th></tr></thead>
          <tbody>{groups.map((g, i) => (
            <tr key={i}>
              <td>{s(g.group) || DASH}</td>
              <td className="num">{n(g.workers)}</td>
              <td>{(Array.isArray(g.tags) ? g.tags : []).map((x) =>
                <code key={String(x)}>{String(x)} </code>)}</td>
              {/* 🔴 A MISSING PING IS NOT A FRESH PING. Absence is not health. */}
              <td className="num">{g.last_ping == null
                ? <span className="muted">never</span> : ago(n(g.last_ping))}</td>
            </tr>))}</tbody>
        </table>), new Set(["groups"])];
    }

    case "Social activity":
      return [kv([
        ["tick", c.last_tick ? <When iso={s(c.last_tick)} />
                             : <span className="warn">never</span>],
        ["last collected", <When iso={s(c.last_success)} />],
        ["window ends", <When iso={s(c.window_end)} />],
        ["in flight", s(c.in_flight) || <span className="muted">nothing</span>],
        ["failures in a row", n(c.consecutive_failures)],
        ["spend today", <>{usd(n(c.spend_est_usd))} est. of {usd(n(c.daily_cap_usd))}</>],
      ]), new Set(["last_tick", "last_run", "last_success", "window_end", "in_flight",
                   "consecutive_failures", "spend_est_usd", "daily_cap_usd"])];

    case "Engines": {
      const es = Array.isArray(c.engines) ? c.engines as Record<string, unknown>[] : [];
      return [(
        <table className="mini">
          <thead><tr><th>Harness</th><th>Model</th><th>Runs as</th></tr></thead>
          <tbody>{es.map((e, i) => (
            <tr key={i}>
              <td><code>{s(e.engine)}</code></td>
              <td><code>{s(e.model)}</code></td>
              <td><code className="small">
                {(Array.isArray(e.target) ? e.target : []).join(" ")}</code></td>
            </tr>))}</tbody>
        </table>), new Set(["engines"])];
    }

    case "LiteLLM gateway":
      return [kv([["base url", c.base_url_set
        ? <Badge tone="ok">set</Badge> : <Badge tone="warn">not set</Badge>]]),
        new Set(["base_url_set"])];

    case "Google credentials": {
      const scopes = Array.isArray(c.scopes) ? c.scopes as string[] : [];
      return [kv([
        ["scopes", <span className="badges">{scopes.map((x) => (
          <span key={x} className="badge plain">{x.split("/").pop()}</span>))}</span>],
        ["teacher credentials", n(c.teacher_credentials)],
      ]), new Set(["scopes", "teacher_credentials"])];
    }

    case "Usage and capacity": {
      // 🔴 USAGE, NOT A PRICE (owner, 2026-10-03). The subscription's own figures, as the last
      // run read them, and the backstop that stops grading when they cannot be read — the
      // reads the poller pauses on and dispatch parks on, never a second query.
      // ⚠️ NOT READ, OR READ BEFORE A RESET, IS NEVER 0%: it says so, and the line where the
      // pause would fall is still drawn.
      const win = (v: unknown) => (v && typeof v === "object"
        ? v as { pct: number; resets_at: string | null; expired: boolean } : null);
      const weekly = win(c.weekly), five = win(c.five_hour);
      // ⚠️ A READING MAY CARRY ONE WINDOW, so "not read yet" is said only when nothing was read:
      // beside "last read: 12 min ago" it was a false statement about the run that did.
      const read = c.read === true;
      const pause = n(c.pause_pct), paused = c.paused === true;
      const ran = n(c.runs_today), perDay = n(c.runs_per_day);
      const fresh = weekly && !weekly.expired ? weekly : null;
      return [(
        <div className="stack tight">
          <Gauge pct={fresh?.pct ?? null} pause={pause} over={paused} />
          {kv([
            ["weekly", fresh
              ? <><b className="num">{pct(fresh.pct)}</b> ·{" "}
                  {paused
                    ? <span className="warn">unattended grading paused at {pct(pause)}</span>
                    : <>pauses unattended grading at {pct(pause)}</>}
                  {pause <= 0 && <span className="warn">, which means stop</span>}
                  {fresh.resets_at && <> · resets {dayAndTime(fresh.resets_at)}</>}</>
              : <span className="muted">{weekly
                  ? <>reset {dayAndTime(weekly.resets_at)}, since the last reading — the next
                      run reads it</>
                  : read
                    ? "not in the last reading — the next run reads it again"
                    : "not read yet — the first run on the updated grader reads it"}
                  {" "}· pauses unattended grading at {pct(pause)}</span>],
            ["5-hour", five && !five.expired
              ? <><b className="num">{pct(five.pct)}</b>
                  {five.resets_at && <> · resets {clockOrDay(five.resets_at)}</>}</>
              : <span className="muted">{five ? "reset since the last reading"
                  : read ? "not in the last reading" : "not read yet"}</span>],
            // 🔴 0 MEANS STOP, NEVER UNLIMITED.
            ["runs today", <><b className="num">{ran}</b> of {perDay}
              {perDay <= 0
                ? <span className="warn"> — 0 means stop: every run is parked</span>
                : <> — every run counts, a teacher’s included</>}</>],
            ["running", <>{n(c.running_now)} of {n(c.max_in_flight)}</>],
            ["last read", c.read_at
              ? <>{since(s(c.read_at), Date.now(), true)}, by the run that finished at{" "}
                  {clock(s(c.read_at))}</>
              : <span className="muted">never</span>],
          ])}
        </div>), new Set(["weekly", "five_hour", "read", "read_at", "read_by", "pause_pct",
                          "paused", "runs_today", "runs_per_day", "running_now",
                          "max_in_flight"])];
    }

    case "Rubrics": {
      const list = (k: string) => (Array.isArray(c[k]) ? c[k] as string[] : []);
      return [kv([
        ["checked", list("checked").length || <span className="muted">none</span>],
        ["mismatched", list("mismatched").length
          ? <span className="warn">{list("mismatched").join("; ")}</span>
          : <span className="muted">none</span>],
        ["unavailable", list("unavailable").length
          ? list("unavailable").map((x) => <div key={x}><code>{x}</code></div>)
          : <span className="muted">none</span>],
      ]), new Set(["checked", "mismatched", "unavailable"])];
    }

    default:
      return [null, new Set<string>()];
  }
}
